
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ApiError } from "@/lib/api/client";
import { testAIConnection } from "@/lib/api/endpoints";
import { Card } from "@/components/ui/Card";
import { StatusPill } from "@/components/ui/StatusPill";
import { putSetting, relativeTime, type TestResult } from "./_shared";
import { LOCAL_CLAUDE_LABEL } from "@/modules/ai/providers";

type ProviderId = "openai" | "anthropic" | "claude-local" | "openrouter";

type TestConnectionResponse = {
  ok: boolean;
  models: string[];
  checkedAt: string;
  error?: string;
};

function looksMasked(s: string): boolean {
  return s.includes("•");
}

function looksLikeAccessError(error: string): boolean {
  const e = error.toLowerCase();
  return (
    e.includes("tier") ||
    e.includes("access") ||
    e.includes("403") ||
    e.includes("gpt-image")
  );
}

async function postTestConnection(
  provider: ProviderId,
): Promise<TestConnectionResponse> {
  // The endpoint returns 200 even for ok:false (key missing / upstream errors).
  // Treat non-2xx as a network/server error.
  try {
    return await testAIConnection({ provider });
  } catch (err) {
    if (err instanceof ApiError) throw new Error(`HTTP ${err.status}`);
    throw err;
  }
}

function ConnectionChip({
  provider,
  testing,
  result,
  networkError,
}: {
  provider: ProviderId;
  testing: boolean;
  result: TestResult | null;
  networkError: string | null;
}) {
  if (testing) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 dark:bg-surface-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
        <span
          aria-hidden
          className="inline-block h-2 w-2 animate-pulse rounded-full bg-zinc-400 dark:bg-zinc-600"
        />
        Testing…
      </span>
    );
  }
  if (networkError) {
    return (
      <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
        ✗ {networkError}
      </span>
    );
  }
  if (!result) return null;
  if (result.ok) {
    // OpenAI Tier-0 specific case: ok=true but zero usable text models.
    if (provider === "openai" && result.models.length === 0) {
      return (
        <span className="rounded-full bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">
          ✓ Key valid · No text models accessible (Tier 0)
        </span>
      );
    }
    if (provider === "openai") {
      return (
        <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          ✓ Connected · {result.models.length} model
          {result.models.length === 1 ? "" : "s"} accessible
        </span>
      );
    }
    if (provider === "openrouter") {
      return (
        <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          ✓ Connected · every OpenRouter model + Jev
        </span>
      );
    }
    // Anthropic: empty models[] is normal — fixed catalogue.
    return (
      <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        ✓ Connected · full catalogue available
      </span>
    );
  }
  return (
    <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
      ✗ {result.error || "Connection failed"}
    </span>
  );
}

function ProviderKeyRow({
  provider,
  providerLabel,
  apiKeyKey,
  initialKeyValue,
  initialKeyMasked,
  initialTestResult,
  keyPlaceholder,
  description,
}: {
  provider: ProviderId;
  providerLabel: string;
  apiKeyKey: string;
  initialKeyValue: string;
  initialKeyMasked: boolean;
  initialTestResult: TestResult | null;
  keyPlaceholder?: string;
  description?: ReactNode;
}) {
  // `keyDisplay` is what's in the input — may be the masked placeholder until
  // the user clicks "Replace" and starts typing a fresh key.
  const [keyDisplay, setKeyDisplay] = useState(initialKeyValue);
  const [isMasked, setIsMasked] = useState(initialKeyMasked);
  // We only PUT the key when the user has explicitly edited it (i.e., we're
  // no longer showing the masked placeholder). `baselineKey` tracks the value
  // we believe is stored server-side, so the dirty flag is accurate.
  const [baselineKey, setBaselineKey] = useState(initialKeyValue);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(initialTestResult);
  const [networkError, setNetworkError] = useState<string | null>(null);
  // Tick to refresh the "Xm ago" label without re-rendering on every keystroke.
  const [, setNow] = useState(0);
  useEffect(() => {
    if (!testResult) return;
    const id = setInterval(() => setNow((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [testResult]);

  // Connected = something is on the server side (masked or plain) and not blank.
  const connected = baselineKey.length > 0;

  const keyDirty = !isMasked && keyDisplay !== baselineKey;
  const dirty = keyDirty;

  const runTest = useCallback(async () => {
    setTesting(true);
    setNetworkError(null);
    try {
      const data = await postTestConnection(provider);
      setTestResult({
        ok: data.ok,
        models: Array.isArray(data.models) ? data.models : [],
        checkedAt: data.checkedAt || new Date().toISOString(),
        error: data.error,
      });
    } catch {
      setNetworkError("Network error");
      // Do NOT update lastCheckedAt on a network failure.
    } finally {
      setTesting(false);
    }
  }, [provider]);

  const onReplace = () => {
    setKeyDisplay("");
    setIsMasked(false);
  };

  const onClear = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await putSetting(apiKeyKey, "");
      setKeyDisplay("");
      setBaselineKey("");
      setIsMasked(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }, [apiKeyKey]);

  const onSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    let savedKey = false;
    try {
      if (keyDirty) {
        await putSetting(apiKeyKey, keyDisplay);
        setBaselineKey(keyDisplay);
        savedKey = true;
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
    // Auto-ping immediately after a successful key save — first save = first test.
    if (savedKey) {
      void runTest();
    }
  }, [apiKeyKey, keyDisplay, keyDirty, runTest]);

  // Show the access-tier hint when an OpenAI error mentions tier / access / 403 /
  // gpt-image (typical Tier-0 symptoms).
  const showAccessHint =
    !testing &&
    !networkError &&
    testResult &&
    !testResult.ok &&
    !!testResult.error &&
    looksLikeAccessError(testResult.error);

  // Show details (model id list) only when OpenAI returns a populated list.
  const showModelList =
    !testing &&
    !networkError &&
    testResult &&
    testResult.ok &&
    provider === "openai" &&
    testResult.models.length > 0;

  const lastCheckedLabel =
    !testing && !networkError && testResult
      ? `Last checked ${relativeTime(testResult.checkedAt)}`
      : null;

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-border p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
            {providerLabel}
          </span>
          <StatusPill connected={connected} />
          <ConnectionChip
            provider={provider}
            testing={testing}
            result={testResult}
            networkError={networkError}
          />
        </div>
        {dirty ? (
          <span className="font-mono text-[10px] uppercase text-amber-600 dark:text-amber-400">
            unsaved
          </span>
        ) : null}
      </div>

      {description ? (
        <p className="mb-2 text-[12px] leading-snug text-zinc-600 dark:text-zinc-400">
          {description}
        </p>
      ) : null}

      <label className="block">
        <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
          API key
        </span>
        <div className="mt-1 flex items-center gap-2">
          <input
            type="password"
            value={keyDisplay}
            onChange={(e) => {
              setKeyDisplay(e.target.value);
              if (isMasked) setIsMasked(false);
            }}
            placeholder={
              keyPlaceholder ??
              (providerLabel === "Anthropic" ? "sk-ant-…" : "sk-…")
            }
            readOnly={isMasked && looksMasked(keyDisplay)}
            className="flex-1 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-2 font-mono text-[12px] text-zinc-900 dark:text-zinc-100 outline-none focus:border-zinc-900 dark:focus:border-zinc-400"
          />
          {isMasked && looksMasked(keyDisplay) ? (
            <button
              type="button"
              onClick={onReplace}
              className="rounded-md border border-zinc-200 dark:border-border px-2 py-1 text-[11px] text-zinc-700 dark:text-zinc-300 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              Replace
            </button>
          ) : null}
          {connected ? (
            <button
              type="button"
              onClick={onClear}
              disabled={saving}
              className="rounded-md border border-red-200 dark:border-red-900/60 px-2 py-1 text-[11px] text-red-700 dark:text-red-300 transition hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-40"
            >
              Clear key
            </button>
          ) : null}
        </div>
      </label>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-3 border-t border-zinc-100 dark:border-border pt-2.5">
        {error ? (
          <span className="text-[12px] text-red-600 dark:text-red-400">{error}</span>
        ) : saved ? (
          <span className="font-mono text-[11px] text-emerald-600 dark:text-emerald-400">Saved ✓</span>
        ) : dirty ? (
          <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400">Unsaved</span>
        ) : null}
        {lastCheckedLabel ? (
          <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500">{lastCheckedLabel}</span>
        ) : null}
        <button
          type="button"
          onClick={runTest}
          disabled={testing || saving}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12px] font-medium text-zinc-700 dark:text-zinc-300 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {testing ? (
            <>
              <span
                aria-hidden
                className="inline-block h-2 w-2 animate-pulse rounded-full bg-zinc-500 dark:bg-zinc-500"
              />
              Testing…
            </>
          ) : (
            "Test connection"
          )}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || saving}
          className="rounded-lg bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 px-3 py-1.5 text-[12px] font-medium text-white transition hover:bg-zinc-800 dark:hover:bg-zinc-200 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      {showAccessHint ? (
        <p className="mt-2 text-[11px] leading-relaxed text-amber-700 dark:text-amber-300">
          This usually means a Tier-0 OpenAI project. Add billing at{" "}
          <span className="font-mono">platform.openai.com</span> to upgrade.
        </p>
      ) : null}

      {showModelList ? (
        <details className="mt-2 rounded-md border border-zinc-100 dark:border-border bg-zinc-50/50 dark:bg-surface/50 px-2 py-1.5">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            {testResult!.models.length} accessible model
            {testResult!.models.length === 1 ? "" : "s"}
          </summary>
          <ul className="mt-1.5 max-h-40 space-y-0.5 overflow-y-auto font-mono text-[11px] text-zinc-700 dark:text-zinc-300">
            {testResult!.models.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local Claude Code ("claude-local") — only rendered when the backend reports
// capabilities.localClaude (running on the user's own machine with
// AI_LOCAL_CLI=true). No key: it uses the Claude Code CLI's own login.
// ---------------------------------------------------------------------------

function looksLikeNotInstalled(error: string): boolean {
  const e = error.toLowerCase();
  return (
    e.includes("not found") ||
    e.includes("not installed") ||
    e.includes("enoent") ||
    e.includes("command not found")
  );
}

function looksLikeNotLoggedIn(error: string): boolean {
  const e = error.toLowerCase();
  return (
    e.includes("not logged in") ||
    e.includes("login") ||
    e.includes("unauthorized") ||
    e.includes("signed in")
  );
}

function LocalClaudeChip({
  testing,
  result,
  networkError,
}: {
  testing: boolean;
  result: TestResult | null;
  networkError: string | null;
}) {
  if (testing) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-100 dark:bg-surface-2 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
        <span
          aria-hidden
          className="inline-block h-2 w-2 animate-pulse rounded-full bg-zinc-400 dark:bg-zinc-600"
        />
        Testing…
      </span>
    );
  }
  if (networkError) {
    return (
      <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
        ✗ {networkError}
      </span>
    );
  }
  if (!result) return null;
  if (result.ok) {
    return (
      <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
        ✓ Ready · Claude Code installed + logged in
      </span>
    );
  }
  const err = result.error ?? "";
  if (looksLikeNotInstalled(err)) {
    return (
      <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
        ✗ Claude Code not installed
      </span>
    );
  }
  if (looksLikeNotLoggedIn(err)) {
    return (
      <span className="rounded-full bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300">
        ⚠ Installed · not logged in
      </span>
    );
  }
  return (
    <span className="rounded-full bg-red-50 dark:bg-red-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-red-700 dark:text-red-300">
      ✗ {err || "Check failed"}
    </span>
  );
}

function LocalClaudeRow({
  initialTestResult,
}: {
  initialTestResult: TestResult | null;
}) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(
    initialTestResult,
  );
  const [networkError, setNetworkError] = useState<string | null>(null);
  const [, setNow] = useState(0);

  useEffect(() => {
    if (!testResult) return;
    const id = setInterval(() => setNow((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [testResult]);

  // Only on click: the check makes one tiny `claude -p` call, which counts
  // against the subscription's usage limits.
  const runTest = useCallback(async () => {
    setTesting(true);
    setNetworkError(null);
    try {
      const data = await testAIConnection({ provider: "claude-local" });
      setTestResult({
        ok: data.ok,
        models: Array.isArray(data.models) ? data.models : [],
        checkedAt: data.checkedAt || new Date().toISOString(),
        error: data.error,
      });
    } catch (err) {
      // 400 = the backend refuses claude-local (AI_LOCAL_CLI off); show why.
      setNetworkError(err instanceof ApiError ? err.message : "Network error");
    } finally {
      setTesting(false);
    }
  }, []);

  const lastCheckedLabel =
    !testing && !networkError && testResult
      ? `Last checked ${relativeTime(testResult.checkedAt)}`
      : null;

  let hint: ReactNode = null;
  if (!testing && !networkError && testResult && !testResult.ok) {
    const err = testResult.error ?? "";
    if (looksLikeNotInstalled(err)) {
      hint = (
        <>
          Install Claude Code (<span className="font-mono">npm i -g @anthropic-ai/claude-code</span>),
          run <span className="font-mono">claude</span> once to log in, then re-test.
        </>
      );
    } else if (looksLikeNotLoggedIn(err)) {
      hint = (
        <>
          Run <span className="font-mono">claude</span> in a terminal and sign in
          with your Claude subscription.
        </>
      );
    }
  }

  return (
    <div className="rounded-xl border border-zinc-200 dark:border-border p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          aria-hidden
          className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-zinc-900 dark:bg-zinc-100 dark:text-zinc-950 font-mono text-[10px] text-emerald-300"
          title="Local CLI"
        >
          &gt;_
        </span>
        <span className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
          {LOCAL_CLAUDE_LABEL}
        </span>
        <span className="rounded-full bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
          No API key
        </span>
        <LocalClaudeChip
          testing={testing}
          result={testResult}
          networkError={networkError}
        />
      </div>

      <p className="text-[12px] leading-snug text-zinc-600 dark:text-zinc-400">
        Runs Claude Code on this computer, so AI rules use your Claude
        subscription (Pro/Max) instead of an API key. Usage counts against your
        subscription&apos;s limits. Pick it per rule in the mapper.
      </p>

      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-3 border-t border-zinc-100 dark:border-border pt-2.5">
        {lastCheckedLabel ? (
          <span className="font-mono text-[10px] text-zinc-400 dark:text-zinc-500">
            {lastCheckedLabel}
          </span>
        ) : null}
        <button
          type="button"
          onClick={runTest}
          disabled={testing}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-border bg-white dark:bg-surface px-3 py-1.5 text-[12px] font-medium text-zinc-700 dark:text-zinc-300 transition hover:border-zinc-900 dark:hover:border-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {testing ? "Testing…" : "Test Claude Code"}
        </button>
      </div>

      {hint ? (
        <p className="mt-2 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">{hint}</p>
      ) : null}
    </div>
  );
}

export function AIProvidersCard({
  anthropicKey,
  anthropicKeyMasked,
  anthropicInitialTest,
  openaiKey,
  openaiKeyMasked,
  openaiInitialTest,
  openrouterKey = "",
  openrouterKeyMasked = false,
  openrouterInitialTest = null,
  localClaude = false,
  claudeLocalInitialTest = null,
}: {
  anthropicKey: string;
  anthropicKeyMasked: boolean;
  anthropicInitialTest: TestResult | null;
  openaiKey: string;
  openaiKeyMasked: boolean;
  openaiInitialTest: TestResult | null;
  openrouterKey?: string;
  openrouterKeyMasked?: boolean;
  openrouterInitialTest?: TestResult | null;
  /** Backend capability (AI_LOCAL_CLI=true). Hosted deployments: false. */
  localClaude?: boolean;
  claudeLocalInitialTest?: TestResult | null;
}) {
  return (
    <Card
      title="AI providers"
      subtitle="Connect Anthropic, OpenAI and/or OpenRouter to power AI mapping rules in templates."
      tone="ai"
    >
      <p className="mb-2 text-[11px] text-zinc-500 dark:text-zinc-400">
        Model + reasoning level are chosen per-rule in the mapper inspector. No
        global default. Stored in your database; keys never leave the server.
      </p>
      <div className="space-y-2">
        <ProviderKeyRow
          provider="anthropic"
          providerLabel="Anthropic"
          apiKeyKey="v2.ai.anthropic.apiKey"
          initialKeyValue={anthropicKey}
          initialKeyMasked={anthropicKeyMasked}
          initialTestResult={anthropicInitialTest}
        />
        <ProviderKeyRow
          provider="openai"
          providerLabel="OpenAI"
          apiKeyKey="v2.ai.openai.apiKey"
          initialKeyValue={openaiKey}
          initialKeyMasked={openaiKeyMasked}
          initialTestResult={openaiInitialTest}
        />
        <ProviderKeyRow
          provider="openrouter"
          providerLabel="OpenRouter"
          apiKeyKey="v2.ai.openrouter.apiKey"
          initialKeyValue={openrouterKey}
          initialKeyMasked={openrouterKeyMasked}
          initialTestResult={openrouterInitialTest}
          keyPlaceholder="sk-or-…"
          description={
            <>
              One key for DeepSeek, Qwen, Gemini, GLM and more, usually much
              cheaper than the direct APIs. It also powers{" "}
              <strong className="font-semibold">Jev</strong> for tagging and
              classifying. Create a key at{" "}
              <span className="font-mono">openrouter.ai/settings/keys</span>.
            </>
          }
        />
      </div>

      {localClaude ? (
        <div className="mt-3 border-t border-zinc-200 dark:border-border pt-3">
          <h3 className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-700 dark:text-zinc-300">
            On this computer
          </h3>
          <LocalClaudeRow initialTestResult={claudeLocalInitialTest} />
        </div>
      ) : null}

    </Card>
  );
}
